import React, { useState } from "react";
import { PlayCircle } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { MediaType } from "../../../types/media";
import { GooglePostProps } from "../types";
import { FaShareAlt } from "react-icons/fa";

const GoogleMyBusinessPost: React.FC<GooglePostProps> = ({
    post,
    setLoading,
    hideDatetime = false,
    contentCharLimit,
}) => {
    // Get the logged-in user from Redux.
    const user = useSelector((state: RootState) => state.auth.user);

    // State for toggling the expanded text view.
    const [isExpanded, setIsExpanded] = useState(false);
    // Adjust this character limit as needed.
    const CHARACTER_LIMIT = 100;

    const toggleExpand = () => {
        setIsExpanded((prev) => !prev);
    };

    // Combine title, content, and footer (with double newlines between sections)
    const getCombinedText = (): string => {
        let text = "";
        if (post?.title) text += post?.title + "\n\n";
        if (post?.content)
            text += Array.isArray(post.content)
                ? post.content.join("\n\n")
                : post?.content + "\n\n";
        if (post?.footer) text += post?.footer;
        return text.trim();
    };

    // Render the full content (with preserved newlines using whitespace-pre-line).
    const renderFullContent = () => {
        const rawContent: string = Array.isArray(post?.content) ? (post?.content as string[]).join("\n\n") : (post?.content as string) || "";
        const withinContent = contentCharLimit ? rawContent.substring(0, contentCharLimit) : rawContent;
        const overflowContent = contentCharLimit && rawContent.length > contentCharLimit ? rawContent.substring(contentCharLimit) : "";
        return (
            <>
                {post?.title && (
                    <div className="text-xl font-bold mb-2 whitespace-pre-line break-words" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {post?.title}
                    </div>
                )}
                {post?.content && (
                    <div className="text-sm text-gray-700 mb-2 whitespace-pre-line break-words" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {withinContent}
                        {overflowContent && (
                            <span className="bg-red-100 line-through text-red-600">{overflowContent}</span>
                        )}
                    </div>
                )}
                {post?.footer && (
                    <div className="text-sm text-gray-700 whitespace-pre-line break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {post?.footer}
                    </div>
                )}
            </>
        );
    };

    // Render content based on the character limit; show a truncated version when not expanded.
    const renderContent = () => {
        const fullText = getCombinedText();
        if (fullText.length <= CHARACTER_LIMIT || isExpanded) {
            return (
                <>
                    {renderFullContent()}
                    {fullText.length > CHARACTER_LIMIT && (
                        <button
                            onClick={toggleExpand}
                            className="text-blue-600 text-sm hover:underline"
                        >
                            Show Less
                        </button>
                    )}
                </>
            );
        } else {
            let truncated = fullText.slice(0, CHARACTER_LIMIT);
            const lastSpace = truncated.lastIndexOf(" ");
            if (lastSpace > 0) {
                truncated = truncated.slice(0, lastSpace);
            }
            return (
                <>
                    <div className="text-sm text-gray-700 whitespace-pre-line break-words" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                        {truncated}...
                    </div>
                    <button
                        onClick={toggleExpand}
                        className="text-blue-600 text-sm hover:underline"
                    >
                        Read More
                    </button>
                </>
            );
        }
    };

    // Render media using Next.js Image for images and a video element if it is a video.
    const renderMedia = () => {
        // If backgroundColor and textColor are present, render styled content block instead of media
        if (post?.background && post?.textColor) {
            // Use imageText for the graphic if available, otherwise use content
            const graphicText = (post as any).imageText || getCombinedText();
            const content = typeof graphicText === 'string' ? graphicText : getCombinedText();

            // Helper function to get background style
            const getBackgroundStyle = (bg: any) => {
                if (!bg) return undefined;
                if (bg.type === 'gradient') {
                    return `linear-gradient(${bg.direction || 'to right'}, ${bg.from || '#000000'}, ${bg.to || '#FFFFFF'})`;
                }
                return bg.color || '#000000';
            };

            return (
                <div className="w-full aspect-[16/9] flex items-center justify-center p-4"
                     style={{ 
                         background: getBackgroundStyle(post.background), 
                         color: post.textColor 
                     }}>
                    <p className="text-base font-semibold text-center leading-relaxed">
                        {content}
                    </p>
                </div>
            );
        }

        if (post?.media && post?.media.length > 0) {
            const media = post?.media[0];
            if (
                media.type === MediaType.AssetVideo ||
                media.type === MediaType.ListingVideo
            ) {
                return (
                    <div className="relative w-full h-full">
                        <video
                            muted
                            src={media.signedUrl}
                            className="object-cover w-full h-full"
                            playsInline
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <PlayCircle className="w-12 h-12 text-white opacity-75" />
                        </div>
                    </div>
                );
            }
            return (
                <div className="w-full relative aspect-[16/9]">
                    <img
                        src={media.signedUrl}
                        alt="Post media"
                        className="object-cover"
                    />
                </div>
            );
        }
        // Fallback placeholder image.
        return (
            <div className="w-full relative aspect-[16/9]">
                <img
                    src="/placeholder.svg?height=400&width=600"
                    alt="Placeholder"
                    className="object-cover"
                />
            </div>
        );
    };

    console.log("Google Post", post);
    return (
        <div className="max-w-lg mx-auto bg-white rounded-lg shadow-md overflow-hidden">
            {/* Header with profile info */}
            <div className="p-4 flex items-center gap-3">
                <div className="relative h-10 w-10 rounded-full overflow-hidden">
                    {post?.userImage ? (
                        <img
                            src={post?.userImage || undefined}
                            width={40}
                            height={40}
                            alt="Profile"
                            className="rounded-full object-cover"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <span 
                        className="text-sm font-semibold flex items-center justify-center w-10 h-10 min-w-[40px] min-h-[40px] bg-gray-300 rounded-full"
                        style={{ display: post?.userImage ? 'none' : 'flex' }}
                    >
                        {post?.displayName
                            ? post.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                            : 'U'}
                    </span>
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-1">
                        <h3 className="text-sm font-medium">
                            {post?.displayName ||
                                `${user?.nameFirst || ""} ${
                                    user?.nameLast || ""
                                }` ||
                                "Unnamed"}
                        </h3>
                    </div>
                    <p className="text-xs text-gray-500">
                        {!hideDatetime && post?.scheduledFor
                            ? new Date(post.scheduledFor).toLocaleDateString()
                            : ""}
                    </p>
                </div>
            </div>

            {/* Main media */}
            {renderMedia()}

            {/* Post content */}
            {/* Don't render content text if using background colors (it's rendered in renderMedia) */}
            {!(post?.background && post?.textColor) && (
                <div className="p-4 space-y-4">{renderContent()}</div>
            )}

            {/* Comment Input */}
            <div className="p-4">
                <div className="flex justify-end items-center gap-2">
                    <FaShareAlt
                        className="text-gray-500 cursor-pointer"
                        size={20}
                    />
                </div>
            </div>
        </div>
    );
};

export default GoogleMyBusinessPost;
